<script lang="ts" setup>
import type { State } from '@autobid/nuxt-pinia-store/types/Search'
import type { DynamicAuctionItemList } from '@autobid/strapi-integration/rest/components'
import { useNoFilterCounters } from '@autobid/ui/composables/car/data/useNoFilterCounters'
import { useSearchCars } from '@autobid/ui/composables/car/data/useSearchCars'
import {
  useCarsListUrlFilter,
} from '@autobid/ui/composables/carsList/useFilter'
import { useDictionary } from '@autobid/ui/composables/useDictionary'
import { useHandleSearchCard } from '@autobid/ui/composables/useSearchCarQuery'
import { useUserApps } from '@autobid/ui/composables/useUserApps'
import { provideFilterComposable } from '@autobid/ui/utils/carsList/provideFilterComposable'
import { filterNotSearchableKeys } from '@autobid/ui/utils/filterNotSearchableKeys'
import { parseQueryString } from '@autobid/ui/utils/parseQueryString'
import { useQuery } from '@tanstack/vue-query'
import { computed, inject, onServerPrefetch } from 'vue'

const auctionStrapiData = inject<DynamicAuctionItemList>('auction-strapi-data')

function useFilter() {
  return useCarsListUrlFilter()
}

provideFilterComposable(useFilter)

const { reset } = useFilter()
const { handleSearchCard } = useHandleSearchCard()
const { userAppIds } = useUserApps()
const route = useRoute()

const mappedDefaults = auctionStrapiData?.defaultFilters ? parseQueryString(auctionStrapiData?.defaultFilters) : {}
const queryParams = computed(() => ({ ...filterNotSearchableKeys(route.query), ...mappedDefaults }))

const CACHE_TIME = 1000 * 60 * 60
const { data: carIds, suspense: suspenseIds } = useQuery({
  queryKey: computed(() => ['search-results-ids', queryParams]),
  queryFn: async (): Promise<number[]> => {
    const resp = await handleSearchCard({
      searchValues: queryParams.value as State,
      userAppIds: userAppIds.value,
      justCount: false,
    })

    if (!resp.carIds?.length)
      return []

    return resp.carIds.map(Number)
  },
  staleTime: CACHE_TIME,
  ...(process.client ? { cacheTime: CACHE_TIME } : {}),
  keepPreviousData: true,
  refetchOnWindowFocus: false,
})
await suspenseIds()

const searchCarIds = useState('searchCarIds')
searchCarIds.value = carIds.value

const {
  data: cars,
  error,
  isFetching,
  refetch,
  errorUpdateCount,
  suspense: suspenseCars,
} = useSearchCars(
  carIds,
  {
    pageSize: auctionStrapiData?.itemsPerPage || 20,
  },
  {
    keepPreviousData: true,
  },
)

const { suspense: suspenseNoFilterCounters } = useNoFilterCounters('searchList')
const { suspense: suspenseDictionary } = useDictionary({ type: 'auctions' })
async function dataFetchers() {
  await Promise.all([
    suspenseNoFilterCounters(),
    suspenseDictionary(),
    suspenseCars(),
  ])
}

onServerPrefetch(dataFetchers)
await dataFetchers()

useHead({
  bodyAttrs: {
    class: 'has-car-search',
  },
})
</script>

<template>
  <div>
    <DynamicAuctionItemListInner
      v-if="cars"
      :cars="cars.items"
      :pages="cars.itemPageCount"
      :is-fetching="isFetching"
    >
      <template #beforeAll>
        <DynamicAuctionItemListSearchTop
          :results-amount="cars.metadata?.counters?.total ?? 0"
        />
      </template>

      <template #leftSide>
        <DynamicAuctionItemListFilters
          :show-deliverer="false"
          :counters="cars.metadata.counters"
        />
      </template>
    </DynamicAuctionItemListInner>

    <LazyDynamicAuctionItemListErrorState
      v-else-if="error"
      :error="error"
      :error-update-count="errorUpdateCount"
      @reset="reset"
      @refetch="refetch"
    />
  </div>
</template>
